// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { toFormOptionalIban } from '@client/banks/utils/account-reference';
import type { BankUploadTemplateEntity } from '@client/banks/utils/download-bank-upload-template';
import { bankAccountSchema } from '@client/banks/types/account';
import {
  bankDebtAmortizationSchema,
  bankDebtSchema,
  BankDebtProductType,
} from '@client/banks/types/debt';
import { bankPositionSchema } from '@client/banks/types/position';
import { bankTransactionSchema } from '@client/banks/types/transaction';
import { isDefined, isNullOrUndefined, isUndefined } from '@shared/utils';
import { z } from 'zod';
import type { $ZodIssue } from 'zod/v4/core';
import {
  normalizeExcelDate,
  normalizeExcelNumber,
  normalizeExcelText,
} from './normalize-excel-cell';
import type {
  BankUploadTemplateLocale,
  ParserMappingDocument,
  ParserMappingField,
} from './types';

const resolveEnumValue = (
  mapping: ParserMappingDocument,
  enumKey: string | undefined,
  value: unknown,
  locale: BankUploadTemplateLocale,
): string | undefined => {
  const text = normalizeExcelText(value);

  if (isNullOrUndefined(text) || isUndefined(enumKey) || enumKey === '') {
    return text;
  }

  const enumDef = mapping.enums[enumKey];

  if (isUndefined(enumDef)) {
    return text;
  }

  if (enumDef.ids.includes(text)) {
    return text;
  }

  return enumDef.labelToId[locale]?.[text] ?? text;
};

export const normalizeRawFieldValue = (
  field: ParserMappingField,
  value: unknown,
  mapping: ParserMappingDocument,
  locale: BankUploadTemplateLocale,
): unknown => {
  switch (field.type) {
    case 'text':
      return normalizeExcelText(value);
    case 'money':
    case 'qty':
    case 'int': {
      const number = normalizeExcelNumber(value);

      if (isNullOrUndefined(number)) {
        return undefined;
      }

      return field.type === 'int' ? Math.trunc(number) : number;
    }
    case 'date':
      return normalizeExcelDate(value);
    case 'datetime':
      return normalizeExcelDate(value, { withTime: true });
    case 'enum':
    case 'enum_soft':
      return resolveEnumValue(mapping, field.enum, value, locale);
    default:
      return normalizeExcelText(value);
  }
};

export const buildRawRowRecord = (
  keys: string[],
  cells: unknown[],
  fields: ParserMappingField[],
  mapping: ParserMappingDocument,
  locale: BankUploadTemplateLocale,
): Record<string, unknown> => {
  const fieldByKey = new Map(fields.map(field => [field.key, field]));

  return keys.reduce<Record<string, unknown>>((record, key, index) => {
    if (key === '') {
      return record;
    }

    const field = fieldByKey.get(key);

    if (isUndefined(field)) {
      return record;
    }

    return {
      ...record,
      [key]: normalizeRawFieldValue(field, cells[index], mapping, locale),
    };
  }, {});
};

const mapAccountRow = (raw: Record<string, unknown>) => {
  const ownerName = normalizeExcelText(raw.owner_name);

  return bankAccountSchema.safeParse({
    iban: toFormOptionalIban(typeof raw.iban === 'string' ? raw.iban : ''),
    name: raw.name,
    currency: raw.currency,
    accountType: raw.account_type,
    available: raw.available,
    balanceDate: raw.balance_date,
    owners: isDefined(ownerName) ? [{ name: ownerName }] : [],
  });
};

const mapPositionRow = (raw: Record<string, unknown>) =>
  bankPositionSchema.safeParse({
    iban: toFormOptionalIban(typeof raw.iban === 'string' ? raw.iban : ''),
    isin: raw.isin,
    name: raw.name,
    currency: raw.currency,
    assetType: raw.asset_type,
    quantity: raw.quantity,
    price: raw.price,
    acquisitionCost: raw.acquisition_cost,
    snapshotDate: raw.snapshot_date,
  });

const mapTransactionRow = (raw: Record<string, unknown>) =>
  bankTransactionSchema.safeParse({
    iban: toFormOptionalIban(typeof raw.iban === 'string' ? raw.iban : ''),
    transactionDate: raw.transaction_date,
    amount: raw.amount,
    currency: raw.currency,
    description: raw.description,
    name: raw.name,
    type: raw.type,
    isin: raw.isin,
    quantity: raw.quantity,
    price: raw.price,
    assetType: raw.asset_type,
    subtype: raw.subtype,
  });

const mapDebtRow = (raw: Record<string, unknown>) => {
  const productType = raw.product_type;

  const pendingAmount =
    productType === BankDebtProductType.CreditLine &&
    isNullOrUndefined(raw.pending_amount) &&
    isDefined(raw.disposed)
      ? raw.disposed
      : raw.pending_amount;

  return bankDebtSchema.safeParse({
    iban: toFormOptionalIban(typeof raw.iban === 'string' ? raw.iban : ''),
    productType,
    name: raw.name,
    currency: raw.currency,
    pendingAmount,
    snapshotDate: raw.snapshot_date,
    contractNumber: raw.contract_number,
    grantedCapital: raw.granted_capital,
    interestRate: raw.interest_rate,
    limitAmount: raw.limit_amount,
    constitutionDate: raw.constitution_date,
    expirationDate: raw.expiration_date,
    nextInstallment: raw.next_installment,
    pendingReceipts: raw.pending_receipts,
    available: raw.available,
    disposed: raw.disposed,
    currentBalance: raw.current_balance,
    nextPaymentDate: raw.next_payment_date,
  });
};

const mapAmortizationRow = (raw: Record<string, unknown>) =>
  bankDebtAmortizationSchema.safeParse({
    installmentDate: raw.installment_date,
    status: raw.status,
    amortized: raw.amortized,
    interest: raw.interest,
    total: raw.total,
    pendingCapital: raw.pending_capital,
  });

export type AmortizationLink = {
  debtIban: string;
  contractNumber: string;
};

const amortizationLinkSchema = z.object({
  debtIban: z.string().min(1),
  contractNumber: z.string().min(1),
});

export const mapAmortizationRaw = (
  raw: Record<string, unknown>,
):
  | {
      ok: true;
      link: AmortizationLink;
      data: ReturnType<typeof bankDebtAmortizationSchema.parse>;
    }
  | { ok: false; issues: $ZodIssue[] } => {
  const linkParsed = amortizationLinkSchema.safeParse({
    debtIban: normalizeExcelText(raw.debt_iban),
    contractNumber: normalizeExcelText(raw.contract_number),
  });

  const parsed = mapAmortizationRow(raw);

  if (!linkParsed.success || !parsed.success) {
    return {
      ok: false,
      issues: [
        ...(linkParsed.success ? [] : linkParsed.error.issues),
        ...(parsed.success ? [] : parsed.error.issues),
      ],
    };
  }

  return {
    ok: true,
    link: linkParsed.data,
    data: parsed.data,
  };
};

const ENTITY_MAPPERS: Record<
  BankUploadTemplateEntity,
  (raw: Record<string, unknown>) => ReturnType<z.ZodType['safeParse']>
> = {
  accounts: mapAccountRow,
  positions: mapPositionRow,
  transactions: mapTransactionRow,
  debts: mapDebtRow,
};

export const mapEntityRow = (
  entity: BankUploadTemplateEntity,
  raw: Record<string, unknown>,
) => ENTITY_MAPPERS[entity](raw);
